import type { Sites } from '@ps/platform-message-framework/common/Sites';
import { request as apiRequest } from '@ps/web-core-api';
import type { RequestError, RequestOptions } from '@ps/web-core-api';
import type { ErrorCode } from '@ps/web-core-enums';
import debounce from 'debounce-promise';
import type { Required } from 'utility-types';

import {
  TXTCLI_User_ID_XN0X_is_not_availlable_ELL,
  TXTWCL_Unable_to_validate_usernameX,
  TXTWCL_Your_UserID_must_not_contain_s_ELL
} from '../translations';
import type { Error, Validator } from '../types';
import { containsOnlyEnglishCharacters } from '../utils/containsOnlyEnglishCharacters';
import {
  START_WITH_BANNED_WORDS,
  USER_ID_FRANCE_REGEX,
  WHITE_START_OR_END_REGEXP
} from '../utils/regex';

export const getCheckUserId = (
  options: Required<RequestOptions>,
  staticValidators?: Validator<Error>[],
  site?: Sites
) => {
  const suggestedNamesCache = Object.create(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let request: any;
  let prevValue: string;

  const checkUserIdAsync: Validator = async (value: string): Promise<Error> => {
    const unableToValidateUsername: Error = {
      valid: false,
      message: { ...TXTWCL_Unable_to_validate_usernameX, values: { 0: value } }
    };
    if (!containsOnlyEnglishCharacters(value)) {
      return unableToValidateUsername;
    }
    if (START_WITH_BANNED_WORDS.test(value)) {
      return {
        valid: false,
        message: { ...TXTCLI_User_ID_XN0X_is_not_availlable_ELL, values: { 0: value } }
      };
    }

    if (staticValidators) {
      const validationResult = staticValidators.find(
        (staticValidator) => !staticValidator(value).valid
      );
      if (validationResult) {
        return validationResult(value);
      }
    }

    if (!value || value.length < 4 || WHITE_START_OR_END_REGEXP.test(value)) {
      return { valid: true, suggestedNames: [] };
    }

    if (value in suggestedNamesCache) {
      return {
        valid: false,
        message: { ...TXTCLI_User_ID_XN0X_is_not_availlable_ELL, values: { 0: value } },
        suggestedNames: suggestedNamesCache[value]
      };
    }

    if (USER_ID_FRANCE_REGEX.test(value)) {
      return {
        valid: false,
        message: TXTWCL_Your_UserID_must_not_contain_s_ELL
      };
    }

    if (value !== prevValue) {
      request = apiRequest({
        ...options,
        service: '/api/v1-preview/registration/userId/validate',
        method: 'POST',
        http: true,
        body: { userId: value }
      });
    }
    prevValue = value;

    try {
      const {
        body: { available, suggestedNames = [] }
      } = await request;

      if (!available) {
        suggestedNamesCache[value] = suggestedNames;
        return {
          valid: false,
          message: { ...TXTCLI_User_ID_XN0X_is_not_availlable_ELL, values: { 0: value } },
          suggestedNames
        };
      }
      return { valid: true };
    } catch (err) {
      const { body } = err as RequestError;
      return {
        ...unableToValidateUsername,
        code: body.code as ErrorCode
      };
    }
  };

  return debounce(checkUserIdAsync, 510); // debounce to match WMT velocity settings of 2 in 2 sec
};
